@if (selectedResult(); as result) {
  <h2 mat-dialog-title class="title">
    <button
      mat-icon-button
      class="back"
      [attr.aria-label]="'AI_ASSISTANT.SHARED.BACK' | translate"
      (click)="backToResults()"
    >
      <mat-icon>arrow_back</mat-icon>
    </button>
    <span class="title-text">{{ data.stat.testCase.name }}</span>
    @if (statusMap.get(result.status); as status) {
      <div class="status" [ngClass]="status.class">
        <mat-icon>{{ status.icon }}</mat-icon>
        {{ status.name | translate }}
      </div>
    }
  </h2>

  <mat-dialog-content class="detail-content">
    <ai-evaluator-result-detail [testResult]="result"></ai-evaluator-result-detail>
  </mat-dialog-content>
} @else {
  <h2 mat-dialog-title class="title">
    <span class="title-text">{{ data.stat.testCase.name }}</span>
  </h2>

  <mat-dialog-content class="detail-content">
    <div class="standing">
      <mat-card appearance="outlined">
        <mat-card-content>
          <span class="standing-label">{{ 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.TARGET_LABEL' | translate }}</span>
          <span class="standing-value">{{ data.stat.targetRate | percent }}</span>
        </mat-card-content>
      </mat-card>
      <mat-card appearance="outlined">
        <mat-card-content>
          <span class="standing-label">{{
            'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.ACTUAL_LABEL' | translate: { days: data.stat.evaluationWindowDays }
          }}</span>
          @if (data.stat.successRate !== null) {
            <span class="standing-value">{{
              'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.ACTUAL_VALUE'
                | translate
                  : {
                      passed: data.stat.passedCount | number,
                      evaluated: data.stat.evaluatedCount | number,
                      rate: data.stat.successRate | percent: '1.0-1',
                    }
            }}</span>
          } @else {
            <span
              class="standing-value"
              role="img"
              [attr.aria-label]="
                'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.TARGET_NOT_SCORED'
                  | translate: { days: data.stat.evaluationWindowDays }
              "
              >—</span
            >
          }
        </mat-card-content>
      </mat-card>
    </div>
    @if (data.stat.testCase.scenario || data.stat.testCase.expectation) {
      <mat-expansion-panel class="measured-panel">
        <mat-expansion-panel-header>
          <mat-panel-title>{{ 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.MEASURED_TITLE' | translate }}</mat-panel-title>
        </mat-expansion-panel-header>
        <div class="measured">
          @if (data.stat.testCase.scenario) {
            <div>
              <h3>{{ 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.SCENARIO_LABEL' | translate }}</h3>
              <p>{{ data.stat.testCase.scenario }}</p>
            </div>
          }
          @if (data.stat.testCase.expectation) {
            <div>
              <h3>{{ 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.EXPECTATION_LABEL' | translate }}</h3>
              <p>{{ data.stat.testCase.expectation }}</p>
            </div>
          }
        </div>
      </mat-expansion-panel>
    }
    <mat-divider></mat-divider>

    @if (loading()) {
      <!-- Placeholder rows at the real row height, so the table fills in rather
           than appearing after a blank gap. -->
      <div
        class="results"
        aria-busy="true"
        [attr.aria-label]="'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.LOADING' | translate"
      >
        <div class="results-header" aria-hidden="true">
          <span>{{ 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.CONVERSATION_COLUMN' | translate }}</span>
          <span>{{ 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.RESULT_COLUMN' | translate }}</span>
        </div>
        <ul class="result-list" aria-hidden="true">
          @for (placeholder of skeletonRows; track $index) {
            <li>
              <span class="skeleton-row">
                <span class="skeleton skeleton--snippet" [style.width.%]="placeholder"></span>
                <span class="skeleton skeleton--status"></span>
              </span>
            </li>
          }
        </ul>
      </div>
    } @else if (errored()) {
      <glxy-alert type="error">
        {{ 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.RESULTS_LOAD_ERROR' | translate }}
      </glxy-alert>
    } @else if (results().length === 0) {
      <glxy-alert type="info">
        {{ 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.RESULTS_EMPTY' | translate }}
      </glxy-alert>
    } @else {
      <div class="results">
        <div class="results-header" aria-hidden="true">
          <span>{{ 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.CONVERSATION_COLUMN' | translate }}</span>
          <span>{{ 'AI_ASSISTANT.TESTING.JOBS_TO_BE_DONE.RESULT_COLUMN' | translate }}</span>
        </div>

        <ul class="result-list">
          @for (result of results(); track $index) {
            <li>
              <button type="button" class="result-row" (click)="select(result)">
                <span class="conversation">
                  <mat-icon class="modality" inline="true">{{ isVoice(result) ? 'call' : 'chat_bubble' }}</mat-icon>
                  <span class="snippet">{{ snippet(result) }}</span>
                </span>
                @if (statusMap.get(result.status); as status) {
                  <span class="status" [ngClass]="status.class">
                    <mat-icon inline="true">{{ status.icon }}</mat-icon>
                    {{ status.name | translate }}
                  </span>
                }
              </button>
            </li>
          }
        </ul>

        @if (hasMore()) {
          <div class="load-more">
            @if (loadingMore()) {
              <glxy-loading-spinner size="small" inline></glxy-loading-spinner>
            } @else {
              <button mat-stroked-button (click)="loadMore()">
                {{ 'AI_ASSISTANT.SHARED.LOAD_MORE' | translate }}
              </button>
            }
          </div>
        }
      </div>
    }
  </mat-dialog-content>
}

<mat-dialog-actions>
  @if (selectedResult()) {
    <button mat-stroked-button (click)="backToResults()">
      {{ 'AI_ASSISTANT.SHARED.BACK' | translate }}
    </button>
  } @else {
    @if (data.stat.custom) {
      <button mat-flat-button color="warn" class="delete-job" (click)="requestDelete()">
        {{ 'AI_ASSISTANT.SHARED.DELETE' | translate }}
      </button>
    }
    <button mat-stroked-button (click)="close()">{{ 'AI_ASSISTANT.SHARED.CLOSE' | translate }}</button>
    @if (data.stat.custom) {
      <button mat-flat-button color="primary" (click)="requestEdit()">
        {{ 'AI_ASSISTANT.SHARED.EDIT' | translate }}
      </button>
    }
  }
</mat-dialog-actions>
